import EventIcon from "@mui/icons-material/Event";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import VideoCameraFrontIcon from "@mui/icons-material/VideoCameraFront";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowVirtualizer,
  useMaterialReactTable,
} from "material-react-table";
import moment from "moment";
import React, { useEffect, useMemo, useRef } from "react";
import { INTERVIEW_ASSEMBLY_TYPES } from "../../../constants";
import { ArtifactMediaData } from "../../../tanstackQuery/queries/artifacts/type";
import DiscoveryCompactMenuItem from "../discovery-compact-menu-item";
import { useLocation } from "react-router-dom";
import { baseAtom } from "../../../stores/jotai/base";
import { useAtom } from "jotai";
import { getDateFormat } from "../../../util";

interface InterviewListProps {
  interviews: any;
  team: any;
  user: any;
  org: any;
  baseAtomBridge: any;
  shouldSeeExport: boolean;
  showingDrafts: boolean;
  switchDraftMode: () => void;
  selectedID?: string;
  templateList: any;
  artifacts: ArtifactMediaData;
  save: (interview: any, interviewDetails: any, cb: any) => void;
  select: (interview: any) => void;
  remove?: (interviewID: string) => void;
  removeAudio?: (interviewID: string) => void;
  handleRevertDocument?: Function;
  scrubDucmentDialog?: string;
  readyForDraftMessage?: any;
  bridgeSelectInterview: (interviewID: string,selectedInterviewIndex?:number) => void;
  isReadOnly?: boolean;
  bridgeEditInterview?: Function;
  interviewFindings: object;
  theme?: Theme;
  assemblies?: any;
  setOpenInterviewListDrawer?:Function;
  openInterviewListDrawer?:any;
}

export const InterviewList: React.FC<InterviewListProps> = ({
  interviews,
  artifacts,
  bridgeSelectInterview,
  isReadOnly,
  bridgeEditInterview,
  interviewFindings,
  theme,
  assemblies,
  setOpenInterviewListDrawer,
  openInterviewListDrawer
}) => {
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null);
  const { pathname } = useLocation();
  const [base,]=useAtom(baseAtom);

  const getUploadProgressFromInterview = (base: any, interviewID: string) => {
    let result = -1;
    try {
      base?.uppyFiles?.forEach((file: any) => {
        if (file?.meta?.interviewID === interviewID) {
          result = file.progress.percentage;
        }
      });
    } catch (e) {
      result = -1;
    }
    return result >= 100 ? -1 : result;
  };

  const getAssemblyTypeFromInterview = (base, interviewID, assemblies) => {
    let match = undefined;

    if (base === undefined || assemblies === undefined) return undefined;

    for (let i = 0; i < assemblies?.length; i++) {
      const t = assemblies[i];

      if (interviewID !== undefined) {
        if (t.reservation === interviewID || t.interviewID === interviewID) {
          match = t;
        }
      }
    }

    if (match === undefined) return match;

    return match.type;
  };
  
  useEffect(() => {
    if (
      base?.selectedInterview &&
      rowVirtualizerInstanceRef.current &&
      base?.selectedInterviewIndex !== -1 &&
      pathname !== "/"
    ) {
      rowVirtualizerInstanceRef.current.scrollToIndex(base?.selectedInterviewIndex);
    }
  }, [
    pathname,
    interviews,
    rowVirtualizerInstanceRef
  ]);

  /**
   * @description Will add response interface for interview table.
   * @date 2025-02-10
   */
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "interviewee",
        header: "Name",
        size: isReadOnly ? 290 : 125,
        Cell: (cell) => (
          <Typography noWrap>{cell.row.original.interviewee}</Typography>
        ),
      },
      {
        accessorKey: "created",
        header: "#",
        id: "created",
        sortingFn: (rowA, rowB) => {
          return (
            moment(rowA.original.created).valueOf() -
            moment(rowB.original.created).valueOf()
          );
        },
        Header: () => (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Tooltip title="Creation Date">
              <EventIcon sx={{ color: "#828282", fontSize: "0.975rem" }} />
            </Tooltip>
          </Box>
        ),
        size: isReadOnly ? 100 : 85,
        Cell: ({ row }) => (
          <div>{moment(row.original.created).format(getDateFormat())}</div>
        ),
      },
      {
        accessorKey: "insightsCount",
        header: "#",
        sortingFn: (rowA, rowB) => {
          const lengthOne =
            interviewFindings?.[rowA.original.interviewID]?.length ?? 0;
          const lengthTwo =
            interviewFindings?.[rowB.original.interviewID]?.length ?? 0;
          return lengthOne - lengthTwo;
        },
        Header: () => (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Tooltip title="Insights">
              <LightbulbIcon sx={{ color: "#828282", fontSize: "0.975rem" }} />
            </Tooltip>
          </Box>
        ),
        size: isReadOnly ? 55 : 30,
        muiTableHeadCellProps: {
          sx: {
            padding: "0 0 0 10px",
          },
        },
        Cell: ({ row }) => (
          <>{interviewFindings?.[row.original.interviewID]?.length || 0}</>
        ),
      },
      {
        accessorKey: "transcription",
        header: "#",
        Header: () => (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Tooltip title="Has Transcription">
              <RecordVoiceOverIcon
                sx={{ color: "#828282", fontSize: "0.975rem" }}
              />
            </Tooltip>
          </Box>
        ),
        size: isReadOnly ? 70 : 60,
        muiTableHeadCellProps: {
          sx: {
            padding: "0 0 0 10px",
          },
        },
        sortingFn: (rowA, rowB) => {
          const valueA = artifacts?.transcript?.[rowA.original.interviewID]
            ? 1
            : 0;
          const valueB = artifacts?.transcript?.[rowB.original.interviewID]
            ? 1
            : 0;
          return valueB - valueA;
        },
        Cell: ({ row }) => {
          const isSelected = pathname !== "/" && base.selectedInterview === row.original.interviewID;

          if (!artifacts?.transcript?.[row.original.interviewID])
            return <span />;

          let transcriptIcon = (
            <RecordVoiceOverIcon
              sx={{
                color: `${isSelected ? "#FFFFFF" : "#828282"}`,
                width: "15px",
              }}
            />
          );
          const interviewAssemblyType = getAssemblyTypeFromInterview(
            base,
            row.original.interviewID,
            assemblies
          );

          if (interviewAssemblyType === INTERVIEW_ASSEMBLY_TYPES.VIDEO) {
            transcriptIcon = (
              <VideoCameraFrontIcon
                sx={{
                  color: `${isSelected ? "#FFFFFF" : "#828282"}`,
                  width: "15px",
                }}
              />
            );
          }

          const uploadProgress = getUploadProgressFromInterview(
            base,
            row.original.interviewID
          );
          if (uploadProgress !== -1) {
            transcriptIcon = (
              <CircularProgress
                size={15}
                sx={{ color: `${isSelected ? "#FFFFFF" : "#828282"}` }}
                variant="determinate"
                value={uploadProgress}
              />
            );
          }
          return transcriptIcon;
        },
      },
      {
        accessorKey: "actions",
        header: "",
        size: 1,
        enableSorting: false,
        Cell: ({ row }) => {
          const transcript = artifacts?.transcript
            ? artifacts?.transcript[row?.original?.interviewID]
            : undefined;
          const video = artifacts?.video
            ? artifacts?.video?.[row.original?.interviewID]
            : undefined;
          const media = artifacts?.media
            ? artifacts?.media?.[row.original?.interviewID]
            : undefined;
          const notes = artifacts?.note
            ? artifacts?.note?.[row.original?.interviewID]
            : undefined;
          const interviewAssemblyType = getAssemblyTypeFromInterview(
            base,
            row.original.interviewID,
            assemblies
          );
          const isSelected = pathname !== "/" && base.selectedInterview === row.original.interviewID;

          return (
            !isReadOnly && (
              <DiscoveryCompactMenuItem
                edit={() => {
                  //@ts-ignore
                  bridgeEditInterview(row.original);
                }}
                interviewee={row.original?.interviewee}
                interviewID={row.original?.interviewID}
                artifacts={{ notes, transcript, video, media }}
                style={{ ":hover": { textDecoration: "underline" } }}
                renderButton={(renderProps) => {
                  const { onClick, onTouchStart, ...otherRenderProps } =
                    renderProps;

                  return (
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        width: "18px",
                        color:   `${isSelected ? "#FFFFFF" : "#828282"}`,
                      }}
                    >
                      <MoreVertIcon
                        sx={{
                          width: "15px",
                          position: "relative",
                          top: "2px",
                          left: "-2px",
                          color:  `${isSelected ? "#FFFFFF" : "#828282"}`,
                          transform: "rotate(90deg)"
                        }}
                        {...otherRenderProps}
                        onClick={(event) => {
                          event.stopPropagation();
                          onClick(event);
                        }}
                        onTouchStart={(event) => {
                          event.stopPropagation();
                          onTouchStart(event);
                        }}
                      />
                    </div>
                  );
                }}
                interviewAssemblyType={interviewAssemblyType}
              />
            )
          );
        },
      },
    ],
    [interviewFindings, isReadOnly, assemblies, artifacts,base.selectedInterview]
  );

  const table = useMaterialReactTable({
    columns,
    data: interviews || [],
    defaultDisplayColumn: { enableResizing: true },
    enableBottomToolbar: false,
    enableColumnResizing: false,
    enableColumnVirtualization: false,
    enableGlobalFilterModes: false,
    enableFullScreenToggle: false,
    rowVirtualizerInstanceRef,
    muiTableHeadProps: {
      sx: {
        borderBottom: `1px solid inherit !important`,
        boxShadow: "none !important",
        "& tr":{
          boxShadow: "none !important",
        }
      },
    },
    enableMultiSort: false,
    initialState: {
      showGlobalFilter: true,
      sorting: [
        {
          id: "created",
          desc: true,
        },
      ],
    },
    renderTopToolbar: ({ table }) => {
      return <></>;
    },
    enableHiding: false,
    enableDensityToggle: false,
    enablePagination: false,
    enableRowVirtualization: true,
    enableColumnActions: false,
    muiTableContainerProps: {
      sx: {
        maxHeight: `${isReadOnly ? "350px" : "calc(100vh - 350px)"}`,
        width: "600px !important",
      },
    },
    getRowId: (row) => row.interviewID,
    muiTableBodyRowProps: ({ row,staticRowIndex }) => {
      const isSelected =
        row.original.interviewID === base?.selectedInterview;
      return {
        hover: false,
        onClick: () => {
          if (!isSelected || pathname==="/") {
            bridgeSelectInterview(row.original.interviewID,staticRowIndex+1);
          }
          if(openInterviewListDrawer){
            setOpenInterviewListDrawer(false);
          }
        },
        sx: {
          cursor: "pointer",
          backgroundColor:
            !isReadOnly && isSelected ? "rgb(25, 118, 210) !important" : "white",
          ":hover": { backgroundColor: theme.custom.hoverOptionColor },
        },
      };
    },
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        color:
          !isReadOnly &&
          base.selectedInterview === row.original.interviewID
            ? "white"
            : undefined,
      },
    }),
  });

  return <MaterialReactTable table={table} />;
};
